import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Configuration } from '../../../configuration';

@Component({
    selector: 'help-contact-us',
    template: require('./help.contact-us.component.html'),
    styles: [require('../personal.account.shared.css'), require('./help.contact-us.component.css')]
})
export class HelpContactUsComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly liveChatScriptId = 'b2b-live-chat-script';
    private readonly liveChatAuth = 'auth-c568dc42-cde9-4e34-a8c5-500c549a23af';

    private liveChatActivationInFlight = false;
    private liveChatRevealTimeout: number | null = null;
    private liveChatMessageListener: ((event: MessageEvent) => void) | null = null;
    private liveChatObserver: MutationObserver | null = null;

    constructor(private configuration: Configuration) { }

    ngOnInit() {
        document.body.classList.add('personal-account-fit');
    }

    ngAfterViewInit() {
        this.startLiveChat();
    }

    ngOnDestroy() {
        document.body.classList.remove('personal-account-fit');
        this.stopLiveChatMonitoring();
        this.removeLiveChat();
    }

    private startLiveChat(): void {
        if (!this.isLiveChatEnabled()) {
            return;
        }

        this.watchForLiveChatReady();
        this.loadLiveChatScript();
    }

    private isLiveChatEnabled(): boolean {
        return !!(this.configuration.liveChat.channelNumber && this.configuration.liveChat.host);
    }

    private getLiveChatHost(): string {
        return this.configuration.liveChat.host.replace(/\/$/, '');
    }

    private getLiveChatChannel(): string {
        return this.configuration.liveChat.channelNumber;
    }

    private getLiveChatScriptUrl(): string {
        return `${this.getLiveChatHost()}/live_chat.v1.0.0.js?channelNumber=${this.getLiveChatChannel()}`;
    }

    private loadLiveChatScript(): void {
        const existingContainer = document.getElementById('ys-chatbot-container');
        if (existingContainer) {
            this.activateLiveChat();
            return;
        }

        const existingScript = document.getElementById(this.liveChatScriptId) as HTMLScriptElement;
        if (existingScript) {
            return;
        }

        const liveChatScriptUrl = this.getLiveChatScriptUrl();

        // Vendor script reads channelNumber from document.currentScript, which is null when injected dynamically.
        (window as any).YS_CHAT_BOX_STATIC_URL = liveChatScriptUrl;

        const script = document.createElement('script');
        script.id = this.liveChatScriptId;
        script.type = 'text/javascript';
        script.src = liveChatScriptUrl;

        document.body.appendChild(script);
    }

    private watchForLiveChatReady(): void {
        this.stopLiveChatMonitoring();

        const tryActivate = () => this.activateLiveChat();

        this.liveChatMessageListener = (event: MessageEvent) => {
            const messageData = event && event.data ? event.data : {};
            const code = messageData.code;
            const type = messageData.type;
            const from = messageData.from;
            if (code === 'YS_LIVE_CHAT' && from === this.liveChatAuth && type === 'ready') {
                tryActivate();
            }
        };
        window.addEventListener('message', this.liveChatMessageListener);

        const attachIframeLoadListener = (iframe: HTMLIFrameElement) => {
            if (iframe.dataset.liveChatRevealBound === 'true') {
                return;
            }

            iframe.dataset.liveChatRevealBound = 'true';
            iframe.addEventListener('load', tryActivate);
        };

        const existingIframe = document.getElementById('ys-chatbot-iframe') as HTMLIFrameElement;
        if (existingIframe) {
            attachIframeLoadListener(existingIframe);
        }

        this.liveChatObserver = new MutationObserver(() => {
            const iframe = document.getElementById('ys-chatbot-iframe') as HTMLIFrameElement;
            if (!iframe) {
                return;
            }

            attachIframeLoadListener(iframe);
        });
        this.liveChatObserver.observe(document.body, { childList: true, subtree: true });

        this.liveChatRevealTimeout = window.setTimeout(tryActivate, 3000);
    }

    private activateLiveChat(): void {
        const container = document.getElementById('ys-chatbot-container');
        const iframe = document.getElementById('ys-chatbot-iframe') as HTMLIFrameElement;
        if (!container || !iframe || !iframe.contentWindow || container.style.display === 'block') {
            return;
        }

        if (this.liveChatActivationInFlight) {
            return;
        }

        this.liveChatActivationInFlight = true;

        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${this.getLiveChatHost()}/api/v1.0/livechat/config?livechat=${this.getLiveChatChannel()}`);
        xhr.onreadystatechange = () => {
            if (xhr.readyState !== 4) {
                return;
            }

            if (xhr.status !== 200) {
                this.liveChatActivationInFlight = false;
                return;
            }

            try {
                const configData = JSON.parse(xhr.responseText);
                if (!configData || configData.errcode !== 0) {
                    if (configData && configData.errcode === 70548) {
                        console.warn(
                            'Live chat is not available on this domain. CCVoIP only allows whitelisted origins ' +
                            '(e.g. https://tenders.b2bquotetenders.co.uk). localhost cannot load the chat bubble.'
                        );
                    }
                    return;
                }

                const protocol = window.location.protocol.replace(':', '');
                const config = configData.config || {};
                const enableWebrtcInboundCall = config.enable_webrtc_inbound_call || 0;

                iframe.contentWindow.postMessage({
                    code: 'YS_LIVE_CHAT',
                    auth: this.liveChatAuth,
                    type: 'open',
                    id: this.getLiveChatChannel(),
                    config: Object.assign({}, config, {
                        protocol: protocol,
                        enable_webrtc_inbound_call:
                            enableWebrtcInboundCall === 1 && protocol === 'https' ? 1 : 0
                    }),
                    company: configData.company,
                    is_available: configData.is_available,
                    message_config: configData.message_config,
                    is_page_visible: document.visibilityState === 'visible',
                    oem_id: configData.oem_id
                }, '*');

                container.style.display = 'block';
            } catch (e) {
                // allow retry via ready/load/timeout
            }

            this.liveChatActivationInFlight = false;
        };
        xhr.send();
    }

    private stopLiveChatMonitoring(): void {
        if (this.liveChatMessageListener) {
            window.removeEventListener('message', this.liveChatMessageListener);
            this.liveChatMessageListener = null;
        }

        if (this.liveChatObserver) {
            this.liveChatObserver.disconnect();
            this.liveChatObserver = null;
        }

        if (this.liveChatRevealTimeout !== null) {
            window.clearTimeout(this.liveChatRevealTimeout);
            this.liveChatRevealTimeout = null;
        }
    }

    private removeLiveChat(): void {
        const container = document.getElementById('ys-chatbot-container');
        if (container) {
            container.remove();
        }

        const previewContainer = document.getElementById('yslc-preview-container');
        if (previewContainer) {
            previewContainer.remove();
        }

        const previewStyle = document.getElementById('yslc-style');
        if (previewStyle) {
            previewStyle.remove();
        }

        const script = document.getElementById(this.liveChatScriptId);
        if (script) {
            script.remove();
        }

        delete (window as any).YS_CHAT_BOX_STATIC_URL;
        this.liveChatActivationInFlight = false;
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/account.management/help/help.contact-us.component.ts